<template>
  <article class="insight-card" :aria-labelledby="`course-title-${course.id}`">
    <header class="insight-header">
      <div>
        <span class="course-code">{{ course.course_code }}</span>
        <h2 :id="`course-title-${course.id}`">{{ course.course_name }}</h2>
      </div>
      <button
        v-if="removable"
        type="button"
        class="icon-action"
        :aria-label="`${l('Fjern', 'Remove')} ${course.course_name}`"
        @click="$emit('remove')"
      >
        ×
      </button>
    </header>
    <div class="period-line">
      <span class="live-dot" aria-hidden="true"></span
      >{{ l("Nøkkeltall fra", "Key figures from") }}
      <strong>{{ periodLabel }}</strong>
    </div>
    <div v-if="graphSampleCount != null" class="sample-context">
      <span>{{ l("Utvalg for denne grafen", "Sample for this chart") }}</span>
      <SampleSizeBadge :count="graphSampleCount" :locale="locale" always />
    </div>
    <dl class="key-figures">
      <div>
        <dt>{{ l("Snittkarakter", "Average grade") }}</dt>
        <dd>
          {{ format(summary.average, 2)
          }}<small v-if="summary.average != null"> / 5</small>
        </dd>
        <span>{{
          summary.average == null
            ? l("Ikke tilgjengelig som A–F-snitt", "No A–F average available")
            : excludeFails
            ? l("Uten stryk · A = 5", "Excluding fails · A = 5")
            : l("Med stryk · A = 5", "Including fails · A = 5")
        }}</span>
      </div>
      <div>
        <dt>{{ l("Strykandel", "Failure rate") }}</dt>
        <dd>
          {{ summary.failRate == null ? "—" : format(summary.failRate * 100, 1)
          }}<small v-if="summary.failRate != null"> %</small>
        </dd>
        <span>{{ l("Av alle vurderinger", "Of all results") }}</span>
      </div>
      <div>
        <dt>{{ l("Vurderinger", "Results") }}</dt>
        <dd>{{ format(summary.count, 0) }}</dd>
        <span>{{ l("I denne perioden", "In this period") }}</span>
      </div>
    </dl>
    <p v-if="meanSampleCount != null || mixedBasis" class="data-basis">
      <span v-if="meanSampleCount != null"
        >{{ l("Grunnlag for snitt", "Average based on") }}: n =
        {{ format(meanSampleCount, 0) }}
        {{
          excludeFails
            ? l("beståtte A–F-vurderinger", "passing A–F results")
            : l("A–F-vurderinger", "A–F results")
        }}.</span
      >
      <span v-if="mixedBasis">
        {{ l("Bokstavfordelingen bruker", "Letter-grade distribution uses") }}
        {{ format(letterSampleCount, 0) }} {{ l("av", "of") }}
        {{ format(summary.count, 0) }}
        {{
          l(
            "vurderinger. Strykandelen gjelder alle vurderinger.",
            "results. Failure rate covers all results."
          )
        }}</span
      >
    </p>
    <div
      class="card-views"
      :aria-label="l('Velg grafvisning', 'Choose chart view')"
    >
      <button
        v-for="option in modes"
        :key="option.value"
        type="button"
        :aria-pressed="course.mode === option.value"
        @click="$emit('mode', option.value)"
      >
        {{ option.label }}
      </button>
    </div>
    <div class="plot-heading">
      <div>
        <h3>{{ plotTitle }}</h3>
        <p>{{ plotDescription }}</p>
      </div>
    </div>
    <div v-if="course.mode === 'distribution'" class="term-picker">
      <button
        type="button"
        :disabled="termIndex >= terms.length - 1 || termIndex < 0"
        :aria-label="l('Forrige periode', 'Previous period')"
        @click="selectTerm(terms[termIndex + 1]?.key)"
      >
        ←
      </button>
      <label
        ><span class="sr-only">{{
          l("Periode for fordelingen", "Distribution period")
        }}</span
        ><select
          :value="course.selectedTermKey"
          @change="selectTerm($event.target.value)"
        >
          <option v-for="term in terms" :key="term.key" :value="term.key">
            {{ term.label }}
          </option>
        </select></label
      >
      <button
        type="button"
        :disabled="termIndex <= 0"
        :aria-label="l('Neste periode', 'Next period')"
        @click="selectTerm(terms[termIndex - 1]?.key)"
      >
        →
      </button>
    </div>
    <ChartComponent
      v-if="hasData"
      :chart-data="chartData"
      :chart-options="chartOptions"
      :chart-type="course.mode === 'distribution' ? 'bar' : 'line'"
      :accessible-label="`${
        course.course_name
      }. ${plotTitle}. ${plotDescription}. ${l(
        'Tallene finnes i tabellen nedenfor.',
        'Figures are available in the table below.'
      )}`"
      :error-label="
        l(
          'Grafen kunne ikke vises. Åpne talltabellen nedenfor.',
          'Unable to display the chart. Open the data table below.'
        )
      "
    />
    <div v-else class="no-observations" role="status">
      {{
        ambiguousData
          ? l(
              "Flere delutvalg i samme periode. Avgrens til lærested/studieprogram for entydige tall.",
              "Multiple populations in the same period. Restrict the institution/programme for unambiguous figures."
            )
          : l(
              "Ingen registrerte tall for denne visningen.",
              "No observations for this view."
            )
      }}
    </div>
    <div class="insight-footer">
      <span>HK-dir / DBH <span aria-hidden="true">·</span> {{ coverage }}</span>
      <div>
        <button
          v-if="canPlan"
          type="button"
          :aria-pressed="inPlan"
          @click="$emit('plan')"
        >
          {{
            inPlan
              ? l("✓ I studieplan", "✓ In study plan")
              : l("+ Studieplan", "+ Study plan")
          }}</button
        ><button
          type="button"
          :aria-expanded="showTable"
          :aria-controls="`table-${course.id}`"
          @click="showTable = !showTable"
        >
          {{
            showTable
              ? l("Skjul tall", "Hide data")
              : l("Vis som tabell", "View data table")
          }}
          <span aria-hidden="true">{{ showTable ? "−" : "+" }}</span>
        </button>
      </div>
    </div>
    <div
      v-if="showTable"
      :id="`table-${course.id}`"
      class="data-table-wrap"
      tabindex="0"
      :aria-label="
        l(
          'Talltabell, rull horisontalt ved behov',
          'Data table, scroll horizontally if needed'
        )
      "
    >
      <table>
        <caption>
          {{
            course.course_name
          }}
          ·
          {{
            plotTitle
          }}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {{
                course.mode === "distribution"
                  ? l("Karakter", "Grade")
                  : l("Periode", "Period")
              }}
            </th>
            <th v-for="(dataset, i) in chartData.datasets" :key="i" scope="col">
              {{ dataset.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, index) in chartData.labels" :key="index">
            <th scope="row">{{ label }}</th>
            <td v-for="(dataset, i) in chartData.datasets" :key="i">
              {{ tableValue(dataset, index) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </article>
</template>

<script>
import ChartComponent from "./ChartComponent.vue";
import SampleSizeBadge from "./SampleSizeBadge.vue";
import {
  summaryForRow,
  sortTerms,
  termKey,
  numberOrNull,
  metricSampleSize,
  hasAmbiguousTerms,
  uniquePeriodRow,
  distributionForRow,
} from "../utils/gradeStatistics.mjs";
export default {
  name: "CourseInsight",
  components: { ChartComponent, SampleSizeBadge },
  props: {
    course: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    terms: { type: Array, default: () => [] },
    chartData: { type: Object, required: true },
    chartOptions: { type: Object, required: true },
    locale: { type: String, default: "no" },
    excludeFails: Boolean,
    removable: { type: Boolean, default: true },
    canPlan: Boolean,
    inPlan: Boolean,
  },
  emits: ["mode", "term", "remove", "plan"],
  data: () => ({ showTable: false }),
  computed: {
    ambiguousData() {
      return hasAmbiguousTerms(
        this.rows,
        this.course.mode,
        this.course.selectedTermKey
      );
    },
    graphSampleCount() {
      return metricSampleSize(
        this.summaryRow,
        this.course.mode,
        this.excludeFails
      );
    },
    meanSampleCount() {
      return metricSampleSize(this.summaryRow, "historical", this.excludeFails);
    },
    letterSampleCount() {
      const distribution = distributionForRow(this.summaryRow);
      return distribution && !distribution.passFail ? distribution.total : null;
    },
    mixedBasis() {
      return (
        this.letterSampleCount != null &&
        this.letterSampleCount < this.summary.count
      );
    },
    modes() {
      return [
        { value: "distribution", label: this.l("Fordeling", "Distribution") },
        { value: "historical", label: this.l("Utvikling", "Trend") },
        { value: "failRates", label: this.l("Stryk", "Failure rate") },
      ];
    },
    orderedRows() {
      return sortTerms(this.rows);
    },
    summaryRow() {
      return this.course.mode === "distribution"
        ? uniquePeriodRow(this.orderedRows, this.course.selectedTermKey)
        : this.orderedRows.length
        ? uniquePeriodRow(
            this.orderedRows,
            termKey(this.orderedRows[this.orderedRows.length - 1])
          )
        : undefined;
    },
    summary() {
      return summaryForRow(this.summaryRow, this.excludeFails);
    },
    periodLabel() {
      if (!this.summaryRow) return "—";
      return (
        this.terms.find((term) => term.key === termKey(this.summaryRow))
          ?.label || String(this.summaryRow.year)
      );
    },
    termIndex() {
      return this.terms.findIndex(
        (term) => term.key === this.course.selectedTermKey
      );
    },
    plotTitle() {
      return this.course.mode === "distribution"
        ? this.l(
            "Hvordan fordeler karakterene seg?",
            "How are grades distributed?"
          )
        : this.course.mode === "historical"
        ? this.l("Karakterene over tid", "Grades over time")
        : this.l("Strykandelen over tid", "Failure rate over time");
    },
    plotDescription() {
      return this.course.mode === "distribution"
        ? this.l(
            "Prosent av registrerte karakterer · antall i tabellen",
            "Share of recorded grades · counts in the table"
          )
        : this.course.mode === "historical"
        ? this.l(
            "A = 5 · F = 0. Hvert punkt er én registrert periode.",
            "A = 5 · F = 0. Each point is one observed period."
          )
        : this.l(
            "Andel av alle vurderinger · samme skala for alle fag",
            "Share of all results · the same scale for every course"
          );
    },
    hasData() {
      return this.chartData.datasets.some((dataset) =>
        dataset.data.some((value) => numberOrNull(value) != null)
      );
    },
    coverage() {
      const years = this.orderedRows.map((row) => row.year);
      return years.length ? `${Math.min(...years)}–${Math.max(...years)}` : "—";
    },
  },
  methods: {
    l(no, en) {
      return this.locale === "no" ? no : en;
    },
    format(value, decimals = 1) {
      return value == null
        ? "—"
        : new Intl.NumberFormat(this.locale === "no" ? "nb-NO" : "en-GB", {
            maximumFractionDigits: decimals,
          }).format(value);
    },
    selectTerm(key) {
      if (key) this.$emit("term", key);
    },
    tableValue(dataset, index) {
      const value = numberOrNull(dataset.data[index]);
      if (value == null) return "—";
      if (this.course.mode === "distribution")
        return `${this.format(value, 1)} % · ${this.format(
          dataset.rawCounts?.[index],
          0
        )}`;
      return this.course.mode === "failRates"
        ? `${this.format(value * 100, 1)} %`
        : this.format(value, 2);
    },
  },
};
</script>

<style scoped>
.insight-card {
  background: #fff;
  border: 1px solid #dce3d6;
  border-radius: 14px;
  padding: 25px;
  min-width: 0;
  text-align: left;
}
.insight-header {
  min-height: 83px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  align-items: flex-start;
}
.course-code {
  color: #61735f;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.2px;
}
.insight-header h2 {
  font-size: 21px;
  line-height: 1.35;
  letter-spacing: -0.7px;
  font-weight: 600;
  margin: 7px 0 12px;
}
.icon-action {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: 1px solid #e5e9e1;
  border-radius: 50%;
  color: #6c7967;
  font-size: 20px;
  line-height: 1;
}
.icon-action:hover {
  background: #f8eae5;
  color: #9c4534;
}
.period-line {
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
  color: #6b7866;
  font-size: 10px;
}
.period-line strong {
  color: #3c543e;
  font-weight: 500;
}
.live-dot {
  width: 5px;
  height: 5px;
  background: #64875a;
  border-radius: 50%;
  margin-right: 3px;
}
.key-figures {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 22px 0;
  padding: 18px 0;
  border-block: 1px solid #e7ece2;
  gap: 12px;
}
.key-figures > div + div {
  border-left: 1px solid #e7ece2;
  padding-left: 14px;
}
.key-figures dt {
  color: #5e705a;
  font-size: 10px;
}
.key-figures dd {
  font-size: 30px;
  font-weight: 500;
  letter-spacing: -1px;
  margin: 6px 0 4px;
  font-variant-numeric: tabular-nums;
}
.key-figures dd small {
  font-size: 12px;
  font-weight: 400;
  color: #61735f;
  letter-spacing: 0;
}
.key-figures div > span {
  color: #73806c;
  font-size: 9px;
  display: block;
}
.card-views {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #f0f3eb;
  border-radius: 7px;
}
.card-views button {
  flex: 1;
  padding: 9px 5px;
  font-size: 11px;
  border-radius: 4px;
  color: #65745e;
}
.card-views button[aria-pressed="true"] {
  color: #23452f;
  background: #fff;
  font-weight: 600;
  box-shadow: 0 1px 4px #273c2110;
}
.plot-heading {
  margin: 24px 0 16px;
}
.plot-heading h3 {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.2px;
}
.plot-heading p {
  font-size: 11px;
  color: #6b7866;
  line-height: 1.6;
  margin-top: 5px;
}
.term-picker {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 12px;
  gap: 8px;
}
.term-picker label {
  flex: 1;
  max-width: 210px;
}
.term-picker select {
  width: 100%;
  text-align: center;
  border: 1px solid #dce3d6;
  padding: 7px 14px;
  border-radius: 5px;
  font-size: 11px;
  background: #fafbf7;
  appearance: auto;
}
.term-picker button {
  width: 30px;
  height: 30px;
  border-radius: 5px;
  border: 1px solid #dce3d6;
  font-size: 13px;
}
.term-picker button:disabled {
  opacity: 0.35;
  cursor: default;
}
.insight-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
  padding-top: 15px;
  border-top: 1px solid #e7ece2;
  font-size: 9px;
  color: #73806c;
}
.insight-footer > div {
  display: flex;
  gap: 15px;
}
.insight-footer button {
  color: #326348;
  font-size: 10px;
}
.insight-footer button span {
  margin-left: 8px;
}
.data-table-wrap {
  max-height: 310px;
  overflow: auto;
  margin-top: 17px;
  border: 1px solid #e1e7da;
  border-radius: 6px;
}
table {
  border-collapse: collapse;
  width: 100%;
  font-size: 11px;
}
caption {
  text-align: left;
  font-weight: 600;
  padding: 12px;
}
th,
td {
  text-align: left;
  padding: 10px 12px;
  border-top: 1px solid #e7ece2;
}
thead th {
  background: #f4f6ef;
  color: #53664e;
}
td {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.no-observations {
  min-height: 270px;
  display: grid;
  place-items: center;
  color: #6b7866;
  font-size: 13px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
button {
  cursor: pointer;
}
button:focus-visible,
select:focus-visible,
.data-table-wrap:focus-visible {
  outline: 3px solid #397454;
  outline-offset: 3px;
}
@media (max-width: 1000px) {
  .insight-card {
    padding: 18px;
  }
}
@media (max-width: 600px) {
  .insight-card {
    padding: 18px;
  }
  .insight-header h2 {
    font-size: 20px;
  }
  .key-figures {
    gap: 7px;
  }
  .key-figures > div + div {
    padding-left: 9px;
  }
  .key-figures dd {
    font-size: 26px;
  }
  .key-figures dt {
    font-size: 9px;
  }
}
.sample-context {
  margin-top: 14px;
}
.sample-context > span {
  display: block;
  margin-bottom: 5px;
  font-size: 10px;
  color: #7a6b4f;
}
.data-basis {
  margin: -7px 0 20px;
  color: #6a7862;
  font-size: 10px;
  line-height: 1.8;
}
</style>
